import { Contacts, Dashboard, Place } from '@material-ui/icons';
import { useDeepEqualValue } from '@superdispatch/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAPISocketMessages } from 'shared/api/APISocketMessages';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { useShowTrialExpiredDialog } from 'shared/errors/data/useShowTrialExpiredDialog';
import { trackEvent } from 'shared/helpers/Analytics';
import { LoadsSidebarIcon } from 'shared/icons/LoadsSidebarIcon';
import { ReportsSidebarIcon } from 'shared/icons/ReportsSidebarIcon';
import { SLBSidebarIcon } from 'shared/icons/SLBSidebarIcon';
import { TripsSidebarIcon } from 'shared/icons/TripsSidebarIcon';
import { UsersSidebarIcon } from 'shared/icons/UsersSidebarIcon';
import { SidebarLinkLegacy } from 'shared/layout/sidebar/AppSidebarMenuLegacy';
import { useDriverCountCache } from 'shared/modules/driver/DriverPreferencesAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { LoadOffersLink } from './AppLayoutSidebarLegacy';
import { AppSidebarLoadSuggestionsLink } from './AppSidebarLoadSuggestionsLink';
import { useLoadboardNudgeIndicator } from './useLoadboardNudgeIndicator';
import {
  useUsersCount,
  useUsersCountCache,
} from '../../../carrier/users/data/UsersAPI';
import { useAPISocket } from 'shared/api/APISocket';
import { VehiclesSidebarIcon } from 'shared/icons/VehiclesSidebarIcon';
import { SuperCardIcon } from 'shared/icons/SuperCardIcon';
import { HomeIcon } from 'shared/icons/HomeIcon';
import { ClaimIcon } from 'shared/icons/ClaimIcon';
import { useShipmentsUpdatedIndicator } from 'carrier/vehicles/data/ShipmentsUpdatedIndicator';
interface SidebarItemsProps {
  localPaths?: {
    dashboard?: string;
    home?: string;
    loads?: string;
    contacts?: string;
    users?: string;
    trips?: string;
    tracking?: string;
    loadboard?: string;
    ratings?: string;
    dispatcherProfile?: string;
    carrierProfile?: string;
    reports?: string;
    settings?: string;
    subscriptionDetails?: string;
    logout?: string;
    vehicles?: string;
    superCard?: string;
    claims?: string;
  };
}

export function useSidebarItems({
  localPaths: localPathsProp = {},
}: SidebarItemsProps) {
  const queryClient = useQueryClient();
  const { data: settings } = useCarrierSettings();
  const shouldShowReportsLink = settings?.user.has_access_to_reports;
  const localPaths = useDeepEqualValue(localPathsProp);
  const { isIndicatorVisible } = useLoadboardNudgeIndicator();
  const { isIndicatorVisible: isVehiclesIndicatorVisible } =
    useShipmentsUpdatedIndicator();
  const { updatePendingJoinRequestCount } = useUsersCountCache();
  const isSuperCardPageEnabled = useFlag('supercard_management');
  const shouldShowVehiclesPage = useFlag('ctms_edi_vehicles');
  const shouldShowHomepage = useFlag('ctms_homepage');
  const isReportingEnabled = useFlag('ctms_reporting');
  const shouldShowClaimsPage = useFlag('ctms_claim_management');
  const { data: usersData } = useUsersCount(
    { status: 'join_requested' },
    {
      staleTime: Infinity,
      refetchInterval: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );

  const { setDriverCount } = useDriverCountCache();
  const { closeTrialExpiredDialog } = useShowTrialExpiredDialog();
  const { closeSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();
  useAPISocketMessages<{ join_requests: number }>((payload) => {
    if (payload.meta.type === 'drivers_count') {
      setDriverCount(payload.data.join_requests);
      void queryClient.invalidateQueries({ queryKey: ['drivers'] });
    }
  });

  useAPISocket(
    '/messages/',
    (action: { event: { data: string } | undefined }) => {
      if (!action.event?.data) return;

      const parsedAction = JSON.parse(action.event.data) as {
        meta: { type: string };
        data: { join_requests: number };
      };

      if (parsedAction.meta.type !== 'users_count') return;

      updatePendingJoinRequestCount(parsedAction.data.join_requests);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  );

  const [homeLinks, offersLinks, primaryLinks, secondaryLinks] = useMemo(() => {
    const home: SidebarLinkLegacy[] = shouldShowHomepage
      ? [
          {
            key: 'home',
            label: 'Home',
            href: '/tms/home',
            Icon: HomeIcon,
            localPath: localPaths.home,
          },
        ]
      : [];

    const offers: SidebarLinkLegacy[] = [
      {
        key: 'loadboard',
        label: 'Loadboard',
        href: '/loadboard',
        Icon: SLBSidebarIcon,
        localPath: localPaths.loadboard,
        badgeContent: isIndicatorVisible ? ' ' : undefined,
        badgeType: 'dot',
        onClick: () => {
          closeTrialExpiredDialog();
          closeSubscriptionCancelledDialog();
          trackEvent('Carrier Clicked Loadboard', { has_counter: false });
        },
        dataIntercomTarget: 'loadboard-sidebar-menu-item',
      },
      {
        key: 'suggested-loads',
        label: 'Suggested Loads',
        href: '/tms/suggested-loads',
        RenderComponent: AppSidebarLoadSuggestionsLink,
      },
      {
        key: 'load-offers',
        label: '',
        href: '',
        RenderComponent: LoadOffersLink,
        dataIntercomTarget: 'load-offers-sidebar-menu-item',
      },
    ];

    const primary: SidebarLinkLegacy[] = [
      ...(shouldShowHomepage
        ? []
        : [
            {
              key: 'dashboard',
              label: 'Dashboard',
              href: '/tms/dashboard',
              Icon: Dashboard,
              localPath: localPaths.dashboard,
            },
          ]),
      ...(shouldShowVehiclesPage
        ? [
            {
              key: 'vehicles',
              label: 'Vehicles',
              href: '/tms/vehicles',
              Icon: VehiclesSidebarIcon,
              localPath: localPaths.vehicles,
              badgeContent: isVehiclesIndicatorVisible ? ' ' : undefined,
              badgeType: 'dot' as const,
              onClick: () => {
                trackEvent('Carrier Clicked Vehicles');
              },
            },
          ]
        : []),
      {
        key: 'loads',
        label: 'Loads',
        href: '/tms/loads',
        Icon: LoadsSidebarIcon,
        localPath: localPaths.loads,
      },
      {
        key: 'trips',
        label: 'Trips',
        href: '/tms/trips',
        Icon: TripsSidebarIcon,
        localPath: localPaths.trips,
      },
      {
        key: 'tracking',
        label: 'Tracking',
        href: '/tms/tracking',
        Icon: Place,
        localPath: localPaths.tracking,
      },
    ];

    const secondary: SidebarLinkLegacy[] = [
      {
        key: 'contacts',
        label: 'Contacts',
        href: '/tms/contacts',
        Icon: Contacts,
        localPath: localPaths.contacts,
      },
      {
        key: 'users',
        label: 'Users',
        href: '/tms/users',
        Icon: UsersSidebarIcon,
        localPath: localPaths.users,
        badgeContent: usersData?.all ? usersData.all : '',
      },
      ...(shouldShowClaimsPage
        ? [
            {
              key: 'claims',
              label: 'Claims',
              href: '/tms/claims',
              Icon: ClaimIcon,
              localPath: localPaths.claims,
              onClick: () => {
                trackEvent('Carrier Clicked Claims');
              },
            },
          ]
        : []),
      {
        key: 'reports',
        label: isReportingEnabled ? 'Reporting' : 'Reports',
        Icon: ReportsSidebarIcon,
        hidden: !shouldShowReportsLink,
        href: '/tms/reports',
        localPath: localPaths.reports,
      },
      ...(isSuperCardPageEnabled
        ? [
            {
              key: 'super-card',
              label: 'SuperCard',
              href: '/tms/super-card',
              Icon: SuperCardIcon,
              localPath: localPaths.superCard,
            },
          ]
        : []),
    ];

    return [home, offers, primary, secondary] as const;
  }, [
    localPaths.contacts,
    localPaths.dashboard,
    localPaths.loadboard,
    localPaths.loads,
    localPaths.reports,
    localPaths.tracking,
    localPaths.trips,
    localPaths.users,
    localPaths.vehicles,
    localPaths.superCard,
    localPaths.home,
    localPaths.claims,
    shouldShowReportsLink,
    shouldShowClaimsPage,
    shouldShowVehiclesPage,
    isSuperCardPageEnabled,
    isReportingEnabled,
    isIndicatorVisible,
    isVehiclesIndicatorVisible,
    usersData?.all,
    closeTrialExpiredDialog,
    closeSubscriptionCancelledDialog,
    shouldShowHomepage,
  ]);

  return { homeLinks, offersLinks, primaryLinks, secondaryLinks };
}
